import React from 'react';
import { DismissRegular } from '@fluentui/react-icons';

/**
 * The free-text-with-suggestions input behind the country and region fields.
 *
 * Replaces react-autosuggest, which was the only thing pulling that package in. The behaviour it needs
 * is small — prefix-filter a fixed local list, highlight the first match, let the shopper type anything
 * anyway — and an address field genuinely must accept free text: plenty of countries have no region
 * data at all, and a shopper whose region is not in the list still has to be able to check out.
 *
 * The `react-autosuggest__*` class names are deliberately KEPT. They are a styling contract, not an
 * implementation detail: public/themes/custom_dark.css styles the dropdown through them, and a
 * merchant's own customCSS (hosted outside this repo, so ungreppable from here) may do the same.
 * Renaming them would silently break dark mode and any merchant styling that targets them.
 */
export interface IAddressSuggestInputProps {
    value?: string;
    onChange?: (value: string) => void;
    disabled?: boolean;
    placeholder?: string;
    /** Suggestions for the current text. Called only while the list is open. */
    getSuggestions: (query: string) => string[];
    inputStyle?: React.CSSProperties;
    /** Shown at the right of the field when there is no value to clear — the country quick-select chevron. */
    adornment?: React.ReactNode;
    /**
     * Runs before the list's own key handling. Calling preventDefault claims the key, which is how the
     * country field's quick-select menu takes the arrows while it is open.
     */
    onInputKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    ariaLabel?: string;
}

const AddressSuggestInput: React.FC<IAddressSuggestInputProps> = (props) => {
    const { value = '', onChange, getSuggestions, disabled } = props;
    const listId = React.useId();
    const [open, setOpen] = React.useState(false);
    // react-autosuggest ran with highlightFirstSuggestion, so Enter took the top match without arrowing
    // to it first. Kept: it is what makes typing "unit" + Enter land on United States.
    const [highlighted, setHighlighted] = React.useState(0);

    // Nothing until the shopper actually types something: an empty query prefix-matches every entry, so
    // without this the field would drop its whole list open the moment it took focus. This is
    // react-autosuggest's default shouldRenderSuggestions (value.trim() must be non-empty), kept because
    // an address field is filled in far more often than it is browsed.
    const suggestions = React.useMemo(
        () => (open && !disabled && value.trim().length > 0 ? getSuggestions(value) : []),
        [open, disabled, value, getSuggestions]);

    const select = React.useCallback((suggestion: string) => {
        onChange?.(suggestion);
        setOpen(false);
        setHighlighted(0);
    }, [onChange]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        props.onInputKeyDown?.(e);
        if (e.defaultPrevented) return;
        if (!open || suggestions.length === 0) {
            if (e.key === 'Escape') setOpen(false);
            return;
        }
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlighted(i => Math.min(i + 1, suggestions.length - 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlighted(i => Math.max(i - 1, 0));
                break;
            case 'Enter':
                if (highlighted >= 0 && highlighted < suggestions.length) {
                    // Claimed so the surrounding checkout form does not submit on the same Enter.
                    e.preventDefault();
                    select(suggestions[highlighted]);
                }
                break;
            case 'Escape':
                setOpen(false);
                break;
        }
    };

    return (
        <div className='react-autosuggest__container' style={{ width: '100%' }}>
            <div style={{ position: 'relative', width: '100%' }}>
                <input
                    disabled={disabled}
                    placeholder={props.placeholder}
                    value={value}
                    className='address-autosuggest-input'
                    style={props.inputStyle}
                    role='combobox'
                    aria-expanded={open && suggestions.length > 0}
                    aria-controls={listId}
                    aria-autocomplete='list'
                    aria-label={props.ariaLabel}
                    onChange={(e) => {
                        onChange?.(e.target.value);
                        setOpen(true);
                        setHighlighted(0);
                    }}
                    // Deliberately no onFocus opener: clicking into a field that already holds a country
                    // would otherwise open the list on that value's own matches. Typing is what opens it.
                    //
                    // Closing on blur rather than a document listener: the suggestions are selected on
                    // mousedown below, which fires first and cancels the blur, so a click still lands.
                    onBlur={() => setOpen(false)}
                    onKeyDown={handleKeyDown}
                />
                <div style={{
                    position: 'absolute', right: '4px', top: '50%',
                    transform: 'translateY(-50%)', display: 'flex', alignItems: 'center',
                }}>
                    {value && !disabled ? (
                        <button
                            type='button'
                            onMouseDown={(e) => { e.preventDefault(); onChange?.(''); }}
                            style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                padding: '0 2px', display: 'flex', alignItems: 'center', color: '#616161',
                            }}
                            aria-label='Clear'
                        ><DismissRegular fontSize={14} /></button>
                    ) : props.adornment}
                </div>
            </div>
            {suggestions.length > 0 && (
                <div className='react-autosuggest__suggestions-container react-autosuggest__suggestions-container--open'>
                    <ul id={listId} className='react-autosuggest__suggestions-list' role='listbox'>
                        {suggestions.map((suggestion, i) => (
                            <li
                                // Value plus index: a fixed local list should not contain duplicates,
                                // but getSuggestions is caller-supplied and a duplicate would collapse two
                                // rows into one keyed element.
                                key={`${suggestion}-${i}`}
                                role='option'
                                aria-selected={i === highlighted}
                                className={`react-autosuggest__suggestion${i === highlighted ? ' react-autosuggest__suggestion--highlighted' : ''}`}
                                // Mousedown, not click: the input loses focus on mousedown, which would
                                // close the list before a click ever landed.
                                onMouseDown={(e) => { e.preventDefault(); select(suggestion); }}
                                onMouseEnter={() => setHighlighted(i)}
                            >{suggestion}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default AddressSuggestInput;
